import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { type StatutBuild, useMedias, usePublication } from "../api/hooks";

/**
 * L'API stocke ses horodatages au format « 2026-08-16 19:30:00 », en UTC et
 * sans suffixe de fuseau. Passé tel quel à `new Date`, JavaScript le lirait
 * comme une heure locale et décalerait tout d'une ou deux heures selon la
 * saison. Le « T » et le « Z » rétablissent la lecture correcte.
 */
function formaterHorodatage(horodatage: string): string {
  const date = new Date(`${horodatage.replace(" ", "T")}Z`);
  if (Number.isNaN(date.getTime())) return horodatage;

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

const LIBELLES_STATUT: Record<StatutBuild, string> = {
  en_cours: "En cours",
  succes: "Réussie",
  echec: "Échouée",
};

const COULEURS_STATUT: Record<StatutBuild, "info" | "success" | "error"> = {
  en_cours: "info",
  succes: "success",
  echec: "error",
};

function Carte({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h4" component="h2" gutterBottom>
          {titre}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function DernierePublication() {
  const publication = usePublication();

  if (publication.isPending) return <Skeleton variant="text" height={48} />;
  if (publication.isError) {
    return <Alert severity="warning">État de publication indisponible.</Alert>;
  }

  const dernier = publication.data.builds[0];

  if (!dernier) {
    return (
      <Typography color="text.secondary">
        Le site n'a encore jamais été publié depuis ici.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {publication.data.enCours && <LinearProgress aria-label="Publication en cours" />}
      <Box>
        <Chip
          size="small"
          label={LIBELLES_STATUT[dernier.statut]}
          color={COULEURS_STATUT[dernier.statut]}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        Démarrée le {formaterHorodatage(dernier.demarreA)}
        {dernier.termineA ? `, terminée le ${formaterHorodatage(dernier.termineA)}` : null}
      </Typography>
    </Stack>
  );
}

function Mediatheque() {
  const medias = useMedias();

  if (medias.isPending) return <Skeleton variant="text" height={48} />;
  if (medias.isError) return <Alert severity="warning">Médiathèque indisponible.</Alert>;

  const total = medias.data.medias.length;

  return (
    <Typography variant="h2" component="p">
      {total}
      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
        {total > 1 ? "médias" : "média"}
      </Typography>
    </Typography>
  );
}

export function TableauDeBord() {
  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Vue d'ensemble du site et des demandes reçues.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        }}
      >
        <Carte titre="Dernière publication">
          <DernierePublication />
        </Carte>

        <Carte titre="Médiathèque">
          <Mediatheque />
        </Carte>

        {/* Les demandes de devis sont bien enregistrées par l'API, mais aucune
            route de lecture n'existe encore : afficher un compteur ici
            reviendrait à interroger une URL qui répond 404. La carte reste,
            vide et honnête, jusqu'à l'écran « Prospects ». */}
        <Carte titre="Demandes du jour">
          <Typography color="text.secondary">
            Le suivi des demandes arrive avec l'écran « Prospects ».
          </Typography>
        </Carte>
      </Box>
    </Stack>
  );
}
